<script setup lang="ts">
/**
 * A modal dialog on the native `<dialog>`, the one of both apps: every dialog of the portal, and on
 * the website the inquiry dialog (design prototype A-77, TL 2026-10-21). Focus trap, Escape and the
 * top layer come from the browser. On phones it is a sheet from the bottom. Escape, the close button
 * and a click on the backdrop emit `close`; the owner decides what closing means. The close button
 * is 36 pixels, 44 on touch screens, its cross in the same place (review RQ1).
 * 440 pixels wide, `width="wide"` 520 like the prototype's inquiry dialog (`dlg-panel`),
 * `width="form"` 680 for the application for the beta, about the column of its form in the
 * prototype (`bf-card`, TL 2026-10-27).
 * `closable` false leaves out the close button and keeps Escape and the backdrop from asking to
 * close: for a dialog with nothing behind it, the login without the guest view (ADR-133 point 3).
 * Browsers with close watchers (Chrome and Edge from 120, Firefox from 132) close a modal dialog
 * on a second Escape without a click or key in between, and that cannot be cancelled; the dialog
 * listens to `close` too (GU1): a closable one asks its owner to close, and whenever the `open`
 * prop still says open afterwards, because the dialog may not be closed or its owner keeps it
 * open while saving, it shows again at once.
 * The slot `icon` puts a mark before the title, such as the team's star (B-124).
 * `describedBy` adds the ids of content that must be read with the dialog, such as a message
 * that is already there when it opens. The dialog is the portal target of the pattern library's
 * panels inside it (`providePortalTarget`): the browser makes the page outside a modal dialog
 * inert, so PixSelect's options must open in here. When the content grows or shrinks while the
 * dialog is open, e.g. another kind of inquiry with another field, the dialog follows it in a
 * short transition instead of jumping (none with `prefers-reduced-motion`).
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useId, useTemplateRef, watch } from "vue";
import PixHeading from "@pixelpoint-pattern-library-v2/base/components/styled/base/primitives/PixHeading/PixHeading.vue";
import { providePortalTarget } from "@pixelpoint-pattern-library-v2/base/lib/composables/usePortalTarget";

const props = withDefaults(
  defineProps<{
    open: boolean;
    title: string;
    description?: string;
    /** further ids for `aria-describedby`, space-separated */
    describedBy?: string | undefined;
    /**
     * More than 440 pixels at most: `wide` 520 for a dialog with more on it (the inquiry dialog),
     * `form` 680 for a long form side by side (the application for the beta)
     */
    width?: "wide" | "form" | undefined;
    /** false: no close button, Escape and the backdrop do nothing */
    closable?: boolean | undefined;
  }>(),
  { closable: true },
);

const emit = defineEmits<{ close: [] }>();

/** The greatest width per `width`; whole class names (AGENTS.md). */
const widthClass = { default: "max-w-110", wide: "max-w-130", form: "max-w-170" } as const;

/** Asks the owner to close, unless the dialog may not be closed. */
function requestClose() {
  if (props.closable) emit("close");
}

const dialog = useTemplateRef<HTMLDialogElement>("dialog");
const content = useTemplateRef<HTMLDivElement>("content");
providePortalTarget(dialog);

/**
 * The content's height while open, which the frame takes with a transition; null while closed and
 * before the first measurement, so the dialog opens at its size without growing into it.
 */
const height = ref<number | null>(null);
let resizeObserver: ResizeObserver | null = null;
const titleId = useId();
const descriptionId = useId();
const describedBy = computed(
  () => [props.description ? descriptionId : "", props.describedBy ?? ""].filter(Boolean).join(" ") || undefined,
);

function sync(open: boolean) {
  const element = dialog.value;
  if (!element) return;
  if (open && !element.open) {
    // jsdom and old engines lack showModal; the attribute keeps the content reachable there.
    if (typeof element.showModal === "function") element.showModal();
    else element.setAttribute("open", "");
  } else if (!open && element.open) {
    if (typeof element.close === "function") element.close();
    else element.removeAttribute("open");
  }
}

/**
 * The browser closed the dialog by itself (GU1). A close by `sync` comes while `open` is false, or
 * after the dialog showed again, and is no news. Otherwise a closable dialog asks its owner, and
 * once the owner has answered, a dialog its prop still holds open shows again.
 */
async function onClosed() {
  if (!props.open || dialog.value?.open) return;
  requestClose();
  await nextTick();
  if (props.open) sync(true);
}

onMounted(() => {
  sync(props.open);
  if (typeof ResizeObserver !== "function" || !content.value) return;
  resizeObserver = new ResizeObserver((entries) => {
    const measured = entries[0]?.target.getBoundingClientRect().height ?? 0;
    // a closed dialog measures 0; keep the frame free until it shows again
    height.value = measured > 0 ? measured : null;
  });
  resizeObserver.observe(content.value);
});
onBeforeUnmount(() => resizeObserver?.disconnect());
watch(
  () => props.open,
  (open) => {
    if (!open) height.value = null;
    sync(open);
  },
);
</script>

<template>
  <dialog
    ref="dialog"
    :class="widthClass[props.width ?? 'default']"
    class="rounded-card text-text-body backdrop:bg-backdrop m-auto w-[calc(100%-2rem)] overflow-visible bg-(--color-surface) p-0 shadow-2xl max-sm:mb-0 max-sm:w-full max-sm:max-w-none max-sm:rounded-b-none"
    :aria-labelledby="titleId"
    :aria-describedby="describedBy"
    @cancel.prevent="requestClose"
    @close="onClosed"
    @click.self="requestClose"
  >
    <div
      class="motion-safe:ease-brand overflow-hidden motion-safe:transition-[height] motion-safe:duration-(--duration-view)"
      :style="height === null ? undefined : { height: `${height}px` }"
      data-dialog-frame
    >
      <div ref="content" class="max-h-[calc(100dvh-2rem)] overflow-y-auto px-6 pt-6 pb-6">
        <div class="flex items-start justify-between gap-4">
          <div class="flex min-w-0 items-center gap-3">
            <slot name="icon" />
            <div class="min-w-0">
              <!-- PixHeading, not `text-heading`: Tailwind reads that class as the colour token, never the size -->
              <PixHeading :id="titleId" :level="2" :appear-as="3" class="m-0 font-extrabold">{{
                props.title
              }}</PixHeading>
              <p v-if="props.description" :id="descriptionId" class="text-ui text-text-soft mt-1 mb-0">
                {{ props.description }}
              </p>
            </div>
          </div>
          <button
            v-if="props.closable"
            type="button"
            class="rounded-pill text-text-soft hover:bg-fill hover:text-text-head focus-visible:outline-focus -mt-1 -mr-2 inline-flex size-(--size-control) flex-none cursor-pointer items-center justify-center focus-visible:outline-2 focus-visible:outline-offset-2 pointer-coarse:-mt-2 pointer-coarse:-mr-3 pointer-coarse:size-(--size-button)"
            aria-label="Schließen"
            @click="requestClose"
          >
            <svg
              class="size-4.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              aria-hidden="true"
            >
              <path d="M6 6l12 12M18 6 6 18" stroke-linecap="round" />
            </svg>
          </button>
        </div>
        <div class="mt-5">
          <slot />
        </div>
      </div>
    </div>
  </dialog>
</template>
